import {
  citiesFiltersConverter,
  exportCsvConverter,
  hostOptionsConverter,
  locationHostFilterDataConverter,
  pagableDataConverter,
} from 'data/convertors/general.converters';
import {
  eventChildcareEditConverter,
  eventFoodProvidedEditConverter,
  addEventPhotoConverter,
  eventCreateConverter,
  eventDetailsConverter,
  eventFilterConverter,
  eventGalleryConverter,
  eventGetConverter,
  eventLocationEditConverter,
  eventShipingAddressConverter,
  eventLocationAddressConverter,
  eventSiteCoordinatorEditConverter,
  eventCoordinatorEditConverter,
  eventClassCreateConverter,
  eventClassAgeGroupEditConverter,
  classCapacityEditConverter,
  eventClassLanguageEditConverter,
  eventClassSessionDateTimeEditConverter,
  eventClassSessionFacilitatorEditConverter,
  deleteEventClassConverter,
  eventDescriptionEditConverter,
  eventTitleEditConverter,
  eventTypeEditConverter,
  eventHostEditConverter,
  duplicateConverter,
  eventCapacityEditConverter,
  eventMeetingLinkEditConverter,
  eventChildCareConverter,
  eventPartDateTimeEditConverter,
  eventsNoteConverter,
  createEventsNoteConverter,
  editEventsNoteConverter,
  deleteEventsNoteConverter,
  eventPartKitOrdersConverter,
  eventShipmentLocationEditConverter,
  eventPartTimeEditConverter,
  addEventRegistrationConverter,
} from 'data/convertors/events.convertors';
import {
  TExportCsvDB,
  TExportCsvUI,
  THostOptionUI,
  TLocationHostFilterDataDB,
  TPageableDataWithContentDB,
  TPageableDataWithContentUI,
  TTableFilterDataUI,
  TTableFilters,
} from 'data/types/generic.types';
import { downloadFile } from 'redux/services/fileServices';
import {
  EEventFilterParams,
  TEventChildcareProvidedEditUI,
  TAddEventPhotoUI,
  TEventDataDB,
  TEventDataUI,
  TExportEventsRequest,
  TEventDetailsDataDB,
  TEventDetailsDataUI,
  TEventFoodProvidedEditUI,
  TEventCreateUI,
  TEventGalleryDB,
  TEventGalleryUI,
  TEventLocationEditUI,
  TEventShipmentLocationEditUI,
  TEventShippingAddressEditUI,
  TEventLocationAddressEditUI,
  TEventSiteCoordinatorEditUI,
  TEventCoordinatorEditUI,
  TEventClassDataUI,
  TEventClassAgeGroupEditUI,
  TClassCapacityEditUI,
  TEventClassDateTimeEditUI,
  TEventClassFacilitatorEditUI,
  TDeleteEventClassUI,
  TEventClassLanguageEditUI,
  TEventDescriptionEditUI,
  TEventTitleEditUI,
  TEventTypeEditUI,
  TEventHostEditUI,
  TDuplicateEventUI,
  TEventCapacityEditUI,
  TEventMeetingLinkEditUI,
  TEventChildCareUI,
  TEventChildCareDB,
  TUpdateCustomQuestionUi,
  TEventRegistrantsUrl,
  TEventPartDateTimeEditUI,
  TEventsNoteDB,
  TEventsNoteUI,
  TEventPartKitOrdersDB,
  TEventPartKitOrdersUI,
  TEventKitOrderHistoryResponse,
  TEventPartTimeEditUI,
  TEventLanguageEditUI,
  TEventTimezoneEditUI,
  TEventAttendanceEditUI,
  TAddEventRegistrationUI,
} from 'data/types/events.types';
import {
  EEventsUrls,
  editMeetingLinkUrl,
  editSessionDateTimeUrl,
  eventPartKitOrdersUrl,
  editPartDateUrl,
  editPartTimeUrl,
} from 'data/urls/events.url';
import { authSplitApi } from 'redux/helpers/slice.helpers';
import { TColumnFilterItem } from 'components/Base/Table/Table.types';
import { kitManagementApi } from './kitManagement.slice';

export type TEventPartKitNoteUI = {
  noteId: string | null;
  note: string;
};

export type TEventPartKitNotesRequest = {
  eventId: string;
  partId: string;
  notes: TEventPartKitNoteUI[];
};

export type TEventPartKitNotesResponse = {
  noteId: string;
  note: string;
  createdAt: string;
};

const baseUrl = `${EEventsUrls.EVENTS}`;
interface GetEventPartKitOrdersParams {
  eventId: string;
  sortField?: string;
  sortDirection?: string;
  kitIds?: string;
  eventPart?: string;
  pageSize?: number;
  page?: number;
}

export const eventsApi = authSplitApi('events', [
  'events',
  'event',
  'event-hosts',
  'event-cities',
  'event-gallery',
  'event-tags',
  'event-location',
  'events-childCare-count',
  'event-part-kit-orders',
  'event-part-kit-notes',
]).injectEndpoints({
  endpoints: build => ({
    createEvent: build.mutation<string, TEventCreateUI>({
      query(body) {
        return {
          url: `${baseUrl}`,
          method: 'POST',
          body: eventCreateConverter.toDb(body),
        };
      },
      invalidatesTags: [
        'events',
        'event-hosts',
        'event-cities',
        'event-location',
      ],
    }),
    createEventClass: build.mutation<string, TEventClassDataUI>({
      query(body) {
        return {
          url: `${baseUrl}/${EEventsUrls.CLASS}`,
          method: 'POST',
          body: eventClassCreateConverter.toDb(body),
        };
      },
      invalidatesTags: ['events', 'event'],
    }),
    deleteEventClass: build.mutation<void, TDeleteEventClassUI>({
      query(body) {
        return {
          url: `${baseUrl}/${EEventsUrls.CLASS}`,
          method: 'DELETE',
          body: deleteEventClassConverter.toDb(body),
        };
      },
      invalidatesTags: ['events', 'event'],
    }),
    // here
    getEvents: build.query<
      TPageableDataWithContentUI<TEventDataUI[]>,
      TTableFilters<EEventFilterParams> & { myEvents?: boolean }
    >({
      query({
        page,
        pageSize,
        sortColumn: { sortDirection, sortField },
        filters,
        myEvents,
      }) {
        return {
          url: baseUrl,
          method: 'GET',
          params: {
            page,
            size: pageSize,
            ...(sortField && sortDirection && { sortField, sortDirection }),
            ...(filters && { ...eventFilterConverter(filters) }),
            ...(myEvents && { myEvents }),
          },
        };
      },
      providesTags: ['events'],
      transformResponse: (data: TPageableDataWithContentDB<TEventDataDB[]>) => {
        return {
          ...pagableDataConverter.fromDb(data),
          content: (data.content || []).map(event =>
            eventGetConverter.fromDb(event),
          ),
        };
      },
    }),
    exportEvents: build.mutation<TExportCsvUI, TExportEventsRequest>({
      query: body => ({
        url: `${baseUrl}/export`,
        method: 'POST',
        body,
      }),
      transformResponse: (data: TExportCsvDB) => {
        const convertedData = exportCsvConverter('csv').fromDb(data);
        downloadFile(convertedData.content, convertedData.fileName);
        return convertedData;
      },
    }),
    getEventById: build.query<TEventDetailsDataUI, { id: string }>({
      query({ id }) {
        return {
          url: `${baseUrl}/${id}`,
          method: 'GET',
        };
      },
      providesTags: ['event'],
      transformResponse: (data: TEventDetailsDataDB) => {
        return eventDetailsConverter.fromDb(data || {});
      },
    }),
    getEventChildCareCountById: build.query<
      TEventChildCareUI[],
      { eventId: string }
    >({
      query({ eventId }) {
        return {
          url: `events/${eventId}/${EEventsUrls.CHILDCARE_COUNT}`,
          method: 'GET',
        };
      },
      providesTags: ['events-childCare-count'],
      transformResponse: (data: TEventChildCareDB[]) => {
        return data?.length
          ? data.map(item => eventChildCareConverter.fromDb(item))
          : [];
      },
    }),
    duplicateEvent: build.mutation<string, TDuplicateEventUI>({
      query(body) {
        return {
          url: `${baseUrl}/${EEventsUrls.DUPLICATE}`,
          method: 'POST',
          body: duplicateConverter.toDb(body),
        };
      },
      invalidatesTags: ['events'],
    }),
    getEventUrl: build.query<string, { eventId: string }>({
      query({ eventId }) {
        return {
          url: `${baseUrl}/${EEventsUrls.URL}`,
          method: 'GET',
          params: {
            eventId,
          },
        };
      },
    }),
    getChildCareUrl: build.query<string, { eventId: string }>({
      query({ eventId }) {
        return {
          url: `${baseUrl}/${EEventsUrls.REGISTRANTS}/${EEventsUrls.CHILDCARE}/${EEventsUrls.URL}`,
          method: 'GET',
          params: {
            eventId,
          },
        };
      },
    }),
    getEventRegistrantsUrl: build.query<string, TEventRegistrantsUrl>({
      query(params) {
        return {
          url: `${baseUrl}/${EEventsUrls.REGISTRANTS}/${EEventsUrls.URL}`,
          method: 'GET',
          params,
        };
      },
    }),
    getEventPhotoGallery: build.query<TEventGalleryUI, void>({
      query() {
        return {
          url: `${baseUrl}/${EEventsUrls.PHOTO}/${EEventsUrls.GALLERY}`,
          method: 'GET',
        };
      },
      providesTags: ['event-gallery'],
      transformResponse: (data: TEventGalleryDB) => {
        return eventGalleryConverter.fromDb(data || {});
      },
    }),
    addEventPhoto: build.mutation<string, TAddEventPhotoUI>({
      query(body) {
        return {
          url: `${baseUrl}/${EEventsUrls.PHOTO}`,
          method: 'PATCH',
          body: addEventPhotoConverter.toDb(body),
        };
      },
      invalidatesTags: ['event', 'events'],
    }),
    getEventCities: build.query<TColumnFilterItem[], void>({
      query() {
        return {
          url: `${baseUrl}/${EEventsUrls.LOCATION}/${EEventsUrls.CITIES}`,
          method: 'GET',
        };
      },
      transformResponse: (data: string[]) =>
        citiesFiltersConverter.fromDb(data),
      providesTags: ['event-cities'],
      keepUnusedDataFor: 3600,
    }),
    getEventHosts: build.query<TTableFilterDataUI[], void>({
      query() {
        return {
          url: `${baseUrl}/hosts-and-elcs`,
          method: 'GET',
        };
      },
      transformResponse: (data: { hosts: TLocationHostFilterDataDB[] }) => {
        return locationHostFilterDataConverter.fromDb(data.hosts);
      },
      providesTags: ['event-hosts'],
      keepUnusedDataFor: 3600,
    }),
    getEventHostOptions: build.query<THostOptionUI[], void>({
      query() {
        return {
          url: `${baseUrl}/hosts-and-elcs`,
          method: 'GET',
        };
      },
      transformResponse: (data: { hosts: TLocationHostFilterDataDB[] }) => {
        return hostOptionsConverter.fromDb(data.hosts);
      },
      providesTags: ['event-hosts'],
      keepUnusedDataFor: 3600,
    }),
    editAgeGroup: build.mutation<void, TEventClassAgeGroupEditUI>({
      query(body) {
        return {
          url: `${baseUrl}/${EEventsUrls.CLASS}/${EEventsUrls.AGE_GROUP}`,
          method: 'PATCH',
          body: eventClassAgeGroupEditConverter.toDb(body),
        };
      },
      invalidatesTags: ['events', 'event'],
    }),
    editCapacity: build.mutation<void, TClassCapacityEditUI>({
      query(body) {
        return {
          url: `${baseUrl}/${EEventsUrls.CLASS}/${EEventsUrls.CAPACITY}`,
          method: 'PATCH',
          body: classCapacityEditConverter.toDb(body),
        };
      },
      invalidatesTags: ['events', 'event'],
    }),
    editEventTimezone: build.mutation<void, TEventTimezoneEditUI>({
      query(body) {
        return {
          url: `${baseUrl}/${EEventsUrls.TIMEZONE}`,
          method: 'PATCH',
          body,
        };
      },
      invalidatesTags: ['events', 'event'],
    }),
    editEventLanguage: build.mutation<void, TEventLanguageEditUI>({
      query(body) {
        return {
          url: `${baseUrl}/${EEventsUrls.LANGUAGE}`,
          method: 'PATCH',
          body,
        };
      },
      invalidatesTags: ['events', 'event'],
    }),
    editLanguage: build.mutation<void, TEventClassLanguageEditUI>({
      query(body) {
        return {
          url: `${baseUrl}/${EEventsUrls.CLASS}/${EEventsUrls.LANGUAGE}`,
          method: 'PATCH',
          body: eventClassLanguageEditConverter.toDb(body),
        };
      },
      invalidatesTags: ['events', 'event'],
    }),
    editSessionDateTime: build.mutation<void, TEventClassDateTimeEditUI>({
      query(body) {
        return {
          url: editSessionDateTimeUrl,
          method: 'PATCH',
          body: eventClassSessionDateTimeEditConverter.toDb(body),
        };
      },
      invalidatesTags: ['events', 'event'],
      onQueryStarted(arg, { dispatch, queryFulfilled }) {
        queryFulfilled
          .then(() => {
            dispatch(
              kitManagementApi.util.invalidateTags(['kit-management-events']),
            );
          })
          .catch(() => {
            // do nothing
          });
      },
    }),
    editPartDate: build.mutation<void, TEventPartDateTimeEditUI>({
      query(body) {
        return {
          url: `${editPartDateUrl}`,
          method: 'PATCH',
          body: eventPartDateTimeEditConverter.toDb(body),
        };
      },
      invalidatesTags: ['events', 'event'],
      onQueryStarted(arg, { dispatch, queryFulfilled }) {
        queryFulfilled
          .then(() => {
            dispatch(
              kitManagementApi.util.invalidateTags(['kit-management-events']),
            );
          })
          .catch(() => {
            // do nothing
          });
      },
    }),
    editPartTime: build.mutation<void, TEventPartTimeEditUI>({
      query(body) {
        return {
          url: `${editPartTimeUrl}`,
          method: 'PATCH',
          body: eventPartTimeEditConverter.toDb(body),
        };
      },
      invalidatesTags: ['events', 'event'],
      onQueryStarted(arg, { dispatch, queryFulfilled }) {
        queryFulfilled
          .then(() => {
            dispatch(
              kitManagementApi.util.invalidateTags(['kit-management-events']),
            );
          })
          .catch(() => {
            // do nothing
          });
      },
    }),
    editSessionFacilitator: build.mutation<void, TEventClassFacilitatorEditUI>({
      query(body) {
        return {
          url: `${baseUrl}/${EEventsUrls.SESSION}/${EEventsUrls.FACILITATOR}`,
          method: 'PATCH',
          body: eventClassSessionFacilitatorEditConverter.toDb(body),
        };
      },
      invalidatesTags: ['events', 'event'],
    }),
    editLocation: build.mutation<void, TEventLocationEditUI>({
      query(body) {
        return {
          url: `${baseUrl}/${EEventsUrls.LOCATION}`,
          method: 'PATCH',
          body: eventLocationEditConverter.toDb(body),
        };
      },
      invalidatesTags: ['events', 'event', 'event-location'],
      onQueryStarted(arg, { dispatch, queryFulfilled }) {
        queryFulfilled
          .then(() => {
            dispatch(
              kitManagementApi.util.invalidateTags([
                'kit-management-events-hosts',
                'kit-management-events-locations',
                'kit-management-parent-locations',
                'kit-management-parent-hosts',
              ]),
            );
          })
          .catch(() => {
            // do nothing
          });
      },
    }),
    editSiteCoordinator: build.mutation<void, TEventSiteCoordinatorEditUI>({
      query(body) {
        return {
          url: `${baseUrl}/${EEventsUrls.SITE_COORDINATOR}`,
          method: 'PATCH',
          body: eventSiteCoordinatorEditConverter.toDb(body),
        };
      },
      invalidatesTags: ['events', 'event'],
    }),
    editEventCoordinator: build.mutation<void, TEventCoordinatorEditUI>({
      query(body) {
        return {
          url: `${baseUrl}/${EEventsUrls.COORDINATOR}`,
          method: 'PATCH',
          body: eventCoordinatorEditConverter.toDb(body),
        };
      },
      invalidatesTags: ['events', 'event'],
    }),
    editEventCapacity: build.mutation<void, TEventCapacityEditUI>({
      query(body) {
        return {
          url: `${baseUrl}/${EEventsUrls.CAPACITY}`,
          method: 'PATCH',
          body: eventCapacityEditConverter.toDb(body),
        };
      },
      invalidatesTags: ['events', 'event'],
    }),
    editEventAttendance: build.mutation<void, TEventAttendanceEditUI>({
      query(body) {
        return {
          url: `${baseUrl}/${EEventsUrls.ATTENDEES}`,
          method: 'PATCH',
          body,
        };
      },
      invalidatesTags: ['events', 'event'],
    }),
    editFoodProvided: build.mutation<void, TEventFoodProvidedEditUI>({
      query(body) {
        return {
          url: `${baseUrl}/${EEventsUrls.FOOD_PROVIDED}`,
          method: 'PATCH',
          body: eventFoodProvidedEditConverter.toDb(body),
        };
      },
      invalidatesTags: ['events', 'event'],
    }),
    editCildcareProvided: build.mutation<void, TEventChildcareProvidedEditUI>({
      query(body) {
        return {
          url: `${baseUrl}/${EEventsUrls.CHILDCARE_PROVIDED}`,
          method: 'PATCH',
          body: eventChildcareEditConverter.toDb(body),
        };
      },
      invalidatesTags: ['events', 'event'],
    }),
    editShippingAddress: build.mutation<void, TEventShippingAddressEditUI>({
      query(body) {
        return {
          url: `${baseUrl}/${EEventsUrls.SHIPMENT_ADDRESS}`,
          method: 'PATCH',
          body: eventShipingAddressConverter.toDb(body),
        };
      },
      invalidatesTags: ['events', 'event'],
    }),
    editLocationAddress: build.mutation<void, TEventLocationAddressEditUI>({
      query(body) {
        return {
          url: `${baseUrl}/${EEventsUrls.LOCATION_ADDRESS}`,
          method: 'PATCH',
          body: eventLocationAddressConverter.toDb(body),
        };
      },
      invalidatesTags: ['events', 'event'],
    }),
    editShipmentLocation: build.mutation<void, TEventShipmentLocationEditUI>({
      query(body) {
        return {
          url: `${baseUrl}/${EEventsUrls.SHIPMENT_LOCATION}`,
          method: 'PATCH',
          body: eventShipmentLocationEditConverter.toDb(body),
        };
      },
      invalidatesTags: ['events', 'event'],
    }),
    publishAction: build.mutation<void, { eventId: string }>({
      query(body) {
        return {
          url: `${baseUrl}/${EEventsUrls.PUBLISH}`,
          method: 'PATCH',
          body,
        };
      },
      invalidatesTags: ['events', 'event'],
      onQueryStarted(arg, { dispatch, queryFulfilled }) {
        queryFulfilled
          .then(() => {
            dispatch(
              kitManagementApi.util.invalidateTags([
                'kit-management-events',
                'kit-management-events-hosts',
                'kit-management-events-locations',
                'kit-management-parent-locations',
                'kit-management-parent-hosts',
              ]),
            );
          })
          .catch(() => {
            // do nothing
          });
      },
    }),
    republishAction: build.mutation<void, { eventId: string }>({
      query(body) {
        return {
          url: `${baseUrl}/${EEventsUrls.REPUBLISH}`,
          method: 'PATCH',
          body,
        };
      },
      invalidatesTags: ['events', 'event'],
      onQueryStarted(arg, { dispatch, queryFulfilled }) {
        queryFulfilled
          .then(() => {
            dispatch(
              kitManagementApi.util.invalidateTags([
                'kit-management-events',
                'kit-management-events-hosts',
                'kit-management-events-locations',
                'kit-management-parent-locations',
                'kit-management-parent-hosts',
              ]),
            );
          })
          .catch(() => {
            // do nothing
          });
      },
    }),
    rescheduleAction: build.mutation<void, { eventId: string }>({
      query(body) {
        return {
          url: `${baseUrl}/${EEventsUrls.RESCHEDULE}`,
          method: 'PATCH',
          body,
        };
      },
      invalidatesTags: ['events', 'event'],
      onQueryStarted(arg, { dispatch, queryFulfilled }) {
        queryFulfilled
          .then(() => {
            dispatch(
              kitManagementApi.util.invalidateTags([
                'kit-management-events',
                'kit-management-events-hosts',
                'kit-management-events-locations',
                'kit-management-parent-locations',
                'kit-management-parent-hosts',
              ]),
            );
          })
          .catch(() => {
            // do nothing
          });
      },
    }),
    cancelAction: build.mutation<void, { eventId: string }>({
      query(body) {
        return {
          url: `${baseUrl}/${EEventsUrls.CANCEL}`,
          method: 'PATCH',
          body,
        };
      },
      invalidatesTags: ['events', 'event'],
      onQueryStarted(arg, { dispatch, queryFulfilled }) {
        queryFulfilled
          .then(() => {
            dispatch(
              kitManagementApi.util.invalidateTags([
                'kit-management-events',
                'kit-management-events-hosts',
                'kit-management-events-locations',
                'kit-management-parent-locations',
                'kit-management-parent-hosts',
              ]),
            );
          })
          .catch(() => {
            // do nothing
          });
      },
    }),
    editDescription: build.mutation<void, TEventDescriptionEditUI>({
      query(body) {
        return {
          url: `${baseUrl}/${EEventsUrls.DESCRIPTION}`,
          method: 'PATCH',
          body: eventDescriptionEditConverter.toDb(body),
        };
      },
      invalidatesTags: ['event'],
    }),
    editTitle: build.mutation<void, TEventTitleEditUI>({
      query(body) {
        return {
          url: `${baseUrl}/${EEventsUrls.TITLE}`,
          method: 'PATCH',
          body: eventTitleEditConverter.toDb(body),
        };
      },
      invalidatesTags: ['events', 'event'],
      onQueryStarted(arg, { dispatch, queryFulfilled }) {
        queryFulfilled
          .then(() => {
            dispatch(
              eventsApi.util.updateQueryData(
                'getEventById',
                { id: arg.id },
                draft => {
                  // BE trims before saving; mirror it so the cached value
                  // matches what a refetch would return
                  draft.title = arg.value.trim();
                },
              ),
            );
          })
          .catch(() => {
            // the error toast is handled by the caller
          });
      },
    }),
    editType: build.mutation<void, TEventTypeEditUI>({
      query(body) {
        return {
          url: `${baseUrl}/${EEventsUrls.TYPE}`,
          method: 'PATCH',
          body: eventTypeEditConverter.toDb(body),
        };
      },
      invalidatesTags: ['events', 'event'],
      onQueryStarted(arg, { dispatch, queryFulfilled }) {
        queryFulfilled
          .then(() => {
            // patch the cached details so the section header (which renders
            // the type) updates without waiting for the refetch
            dispatch(
              eventsApi.util.updateQueryData(
                'getEventById',
                { id: arg.id },
                draft => {
                  draft.type = arg.value as TEventDetailsDataUI['type'];
                },
              ),
            );
          })
          .catch(() => {
            // the error toast is handled by the caller
          });
      },
    }),
    editHost: build.mutation<void, TEventHostEditUI>({
      query(body) {
        return {
          url: `${baseUrl}/${EEventsUrls.HOST}`,
          method: 'PATCH',
          body: eventHostEditConverter.toDb(body),
        };
      },
      invalidatesTags: [
        'events',
        'event',
        'event-hosts',
        'event-cities',
        'event-location',
      ],
      onQueryStarted(arg, { dispatch, queryFulfilled }) {
        queryFulfilled
          .then(() => {
            dispatch(
              kitManagementApi.util.invalidateTags([
                'kit-management-events',
                'kit-management-events-hosts',
                'kit-management-events-locations',
                'kit-management-parent-locations',
                'kit-management-parent-hosts',
              ]),
            );
          })
          .catch(() => {
            // do nothing
          });
      },
    }),
    editMeetingLink: build.mutation<void, TEventMeetingLinkEditUI>({
      query(body) {
        return {
          url: editMeetingLinkUrl,
          method: 'PATCH',
          body: eventMeetingLinkEditConverter.toDb(body),
        };
      },
      invalidatesTags: ['event'],
    }),
    deleteAction: build.mutation<void, { eventId: string }>({
      query(body) {
        return {
          url: `${baseUrl}/${body.eventId}`,
          method: 'DELETE',
        };
      },
      invalidatesTags: ['events', 'event'],
    }),
    getEventLocation: build.query<TTableFilterDataUI[], void>({
      query() {
        return {
          url: `${baseUrl}/${EEventsUrls.LOCATIONS}`,
          method: 'GET',
        };
      },
      transformResponse: (data: TLocationHostFilterDataDB[]) =>
        locationHostFilterDataConverter.fromDb(data),
      providesTags: ['event-location'],
      keepUnusedDataFor: 3600,
    }),
    getEventLocationOptions: build.query<THostOptionUI[], void>({
      query() {
        return {
          url: `${baseUrl}/${EEventsUrls.LOCATIONS}`,
          method: 'GET',
        };
      },
      transformResponse: (data: TLocationHostFilterDataDB[]) =>
        hostOptionsConverter.fromDb(data),
      providesTags: ['event-location'],
      keepUnusedDataFor: 3600,
    }),
    updateCustomQuestion: build.mutation<void, TUpdateCustomQuestionUi>({
      query({ id, question }) {
        return {
          url: `${baseUrl}/${EEventsUrls.CUSTOM_QUESTION}`,
          method: 'PATCH',
          body: {
            eventId: id,
            customQuestion: question,
          },
        };
      },
      invalidatesTags: ['event'],
    }),
    deleteCustomQuestion: build.mutation<void, { id: string }>({
      query({ id }) {
        return {
          url: `${baseUrl}/${EEventsUrls.CUSTOM_QUESTION}`,
          method: 'PATCH',
          body: {
            eventId: id,
            customQuestion: null,
          },
        };
      },
      invalidatesTags: ['event'],
    }),
    closeAction: build.mutation<void, { eventId: string }>({
      query(body) {
        return {
          url: `${baseUrl}/${EEventsUrls.CLOSE}`,
          method: 'POST',
          body,
        };
      },
      invalidatesTags: ['events', 'event'],
    }),
    getSessionAttendanceUrl: build.query<
      string,
      { eventId: string; sessionId: string }
    >({
      query(params) {
        return {
          url: `${baseUrl}/${EEventsUrls.SESSIONS}/${EEventsUrls.REGISTRANTS}/${EEventsUrls.URL}`,
          method: 'GET',
          params,
        };
      },
    }),
    getEventNotes: build.query<TEventsNoteUI[], { eventId: string }>({
      query({ eventId }) {
        return {
          url: `${baseUrl}/${EEventsUrls.NOTES}`,
          method: 'GET',
          params: { eventId },
        };
      },
      providesTags: ['event-notes'],
      transformResponse: (data: TEventsNoteDB[]) => {
        return data?.length
          ? data.map(item => eventsNoteConverter.fromDb(item))
          : [];
      },
    }),
    createEventNotes: build.mutation<string, TEventsNoteUI>({
      query(body) {
        return {
          url: `${baseUrl}/${EEventsUrls.NOTES}`,
          method: 'POST',
          body: createEventsNoteConverter.toDb(body),
        };
      },
      invalidatesTags: ['event-notes'],
    }),
    editEventNote: build.mutation<void, TEventsNoteUI>({
      query(body) {
        return {
          url: `${baseUrl}/${EEventsUrls.NOTES}`,
          method: 'PATCH',
          body: editEventsNoteConverter.toDb(body),
        };
      },
      invalidatesTags: ['event-notes'],
    }),
    deleteEventNote: build.mutation<void, TEventsNoteUI>({
      query(body) {
        return {
          url: `${baseUrl}/${EEventsUrls.NOTES}`,
          method: 'DELETE',
          body: deleteEventsNoteConverter.toDb(body),
        };
      },
      invalidatesTags: ['event-notes'],
    }),
    getEventPartKitOrders: build.query<
      TEventPartKitOrdersUI,
      GetEventPartKitOrdersParams
    >({
      query(params) {
        return {
          url: `${eventPartKitOrdersUrl}`,
          method: 'GET',
          params,
        };
      },
      providesTags: ['event-part-kit-orders'],
      transformResponse: (data: TEventPartKitOrdersDB) => {
        return eventPartKitOrdersConverter.fromDb(data);
      },
    }),
    getEventKitOrderHistory: build.query<TEventKitOrderHistoryResponse, string>(
      {
        query(eventId) {
          return {
            url: `${EEventsUrls.ORDERING}/event/${eventId}/kit-order-history`,
            method: 'GET',
          };
        },
      },
    ),
    getEventPartKitNotes: build.query<
      TEventPartKitNotesResponse[],
      { eventId: string; partId: string }
    >({
      query({ eventId, partId }) {
        return {
          url: `${baseUrl}/${eventId}/part/${partId}/kit/notes`,
          method: 'GET',
        };
      },
      providesTags: (result, error, { eventId, partId }) => [
        { type: 'event-part-kit-notes', id: `${eventId}-${partId}` },
      ],
    }),
    updateEventPartKitNotes: build.mutation<
      void,
      { eventId: string; partId: string; notes: TEventPartKitNoteUI[] }
    >({
      query({ eventId, partId, notes }) {
        return {
          url: `${baseUrl}/${eventId}/part/${partId}/kit/notes`,
          method: 'PUT',
          body: notes,
        };
      },
      invalidatesTags: (result, error, { eventId, partId }) => [
        { type: 'event-part-kit-notes', id: `${eventId}-${partId}` },
      ],
    }),
    deleteEventPartKitNote: build.mutation<
      void,
      { eventId: string; partId: string; noteId: string }
    >({
      query({ eventId, partId, noteId }) {
        return {
          url: `${baseUrl}/${eventId}/part/${partId}/kit/notes`,
          method: 'DELETE',
          body: { noteId },
        };
      },
      invalidatesTags: (result, error, { eventId, partId }) => [
        { type: 'event-part-kit-notes', id: `${eventId}-${partId}` },
      ],
    }),
    exportEventAttendance: build.mutation<
      TExportCsvUI,
      {
        columns: string[];
        filters?: Record<string, unknown>;
        sort?: { field: string; direction: string };
      }
    >({
      query: body => ({
        url: `${baseUrl}/${EEventsUrls.ATTENDANCE}/${EEventsUrls.EXPORT}`,
        method: 'POST',
        body,
      }),
      transformResponse: (data: TExportCsvDB) => {
        const convertedData = exportCsvConverter('csv').fromDb(data);
        downloadFile(convertedData.content, convertedData.fileName);
        return convertedData;
      },
    }),
    exportEventWaitList: build.mutation<
      TExportCsvUI,
      {
        columns: string[];
        filters?: Record<string, unknown>;
        sort?: { field: string; direction: string };
      }
    >({
      query: body => ({
        url: `${baseUrl}/${EEventsUrls.REGISTRANTS}/wait-list/${EEventsUrls.EXPORT}`,
        method: 'POST',
        body,
      }),
      transformResponse: (data: TExportCsvDB) => {
        const convertedData = exportCsvConverter('csv').fromDb(data);
        downloadFile(convertedData.content, convertedData.fileName);
        return convertedData;
      },
    }),
    addEventRegistration: build.mutation<unknown, TAddEventRegistrationUI>({
      query(body) {
        return {
          url: `${baseUrl}/${EEventsUrls.REGISTRATION}`,
          method: 'POST',
          body: addEventRegistrationConverter.toDb(body),
        };
      },
      // Note: cross-slice invalidation of 'event-registrants' tags is intentionally
      // omitted here to avoid a circular import (eventsApi ↔ registrantsApi).
      // As a result, the registrants list on the same tab will not auto-refresh after
      // adding a registration — the user must manually reload the registrants tab.
      // To fix properly, add a listener in registrantsApi (which already imports eventsApi)
      // once createListenerMiddleware scaffolding is added to the store.
      invalidatesTags: ['events', 'event'],
    }),
  }),
});

export const {
  useCreateEventMutation,
  useCreateEventClassMutation,
  useGetEventsQuery,
  useExportEventsMutation,
  useGetEventByIdQuery,
  useGetEventHostsQuery,
  useGetEventHostOptionsQuery,
  useGetEventCitiesQuery,
  useGetEventUrlQuery,
  useGetEventRegistrantsUrlQuery,
  useGetChildCareUrlQuery,
  useEditAgeGroupMutation,
  useEditCapacityMutation,
  useEditEventTimezoneMutation,
  useEditEventLanguageMutation,
  useDeleteEventClassMutation,
  useEditLanguageMutation,
  useEditSessionDateTimeMutation,
  useEditSessionFacilitatorMutation,
  useEditLocationMutation,
  useEditSiteCoordinatorMutation,
  useEditEventCoordinatorMutation,
  useEditFoodProvidedMutation,
  useEditCildcareProvidedMutation,
  useEditShippingAddressMutation,
  useEditLocationAddressMutation,
  useEditShipmentLocationMutation,
  useGetEventPhotoGalleryQuery,
  useAddEventPhotoMutation,
  usePublishActionMutation,
  useRepublishActionMutation,
  useRescheduleActionMutation,
  useCancelActionMutation,
  useEditDescriptionMutation,
  useEditTitleMutation,
  useEditTypeMutation,
  useEditHostMutation,
  useDeleteActionMutation,
  useDuplicateEventMutation,
  useEditEventCapacityMutation,
  useEditEventAttendanceMutation,
  useEditMeetingLinkMutation,
  useGetEventLocationQuery,
  useGetEventLocationOptionsQuery,
  useGetEventChildCareCountByIdQuery,
  useUpdateCustomQuestionMutation,
  useDeleteCustomQuestionMutation,
  useCloseActionMutation,
  useLazyGetSessionAttendanceUrlQuery,
  useEditPartDateMutation,
  useEditPartTimeMutation,
  useGetEventNotesQuery,
  useCreateEventNotesMutation,
  useEditEventNoteMutation,
  useDeleteEventNoteMutation,
  useGetEventPartKitOrdersQuery,
  useLazyGetEventPartKitOrdersQuery,
  useGetEventKitOrderHistoryQuery,
  useGetEventPartKitNotesQuery,
  useUpdateEventPartKitNotesMutation,
  useDeleteEventPartKitNoteMutation,
  useExportEventAttendanceMutation,
  useExportEventWaitListMutation,
  useAddEventRegistrationMutation,
} = eventsApi;
